import { useEffect, useState } from "react";
import { Mail, CheckCircle2, AlertTriangle, Plug } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Loader } from "@/components/portal/ui";

const STATUS_STYLE = {
  sent: { bg: "#DCFCE7", fg: "#15803D" },
  not_configured: { bg: "var(--surface-alt)", fg: "var(--text-muted)" },
  error: { bg: "#FEE2E2", fg: "#B91C1C" },
  skipped: { bg: "var(--surface-alt)", fg: "var(--text-muted)" },
};

export function EmailStatus() {
  const { L } = useI18n();
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState(null);

  useEffect(() => {
    api.get("/control/email/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false, status: "not_configured", events: [] }));
    api.get("/control/email/log").then(({ data }) => setLog(data)).catch(() => setLog([]));
  }, []);

  if (!status || !log) return <Loader />;
  const configured = status.configured;
  const hasRecentError = configured && log.slice(0, 10).some((e) => e.status === "error");
  const state = !configured ? "not_configured" : hasRecentError ? "error" : "connected";
  const BADGE = {
    not_configured: { bg: "var(--surface-alt)", fg: "var(--text-muted)", icon: Plug, label: { de: "Nicht konfiguriert", fr: "Non configuré", it: "Non configurato", en: "Not configured" } },
    connected: { bg: "#DCFCE7", fg: "#15803D", icon: CheckCircle2, label: { de: "Verbunden", fr: "Connecté", it: "Connesso", en: "Connected" } },
    error: { bg: "#FEE2E2", fg: "#B91C1C", icon: AlertTriangle, label: { de: "Fehler", fr: "Erreur", it: "Errore", en: "Error" } },
  };
  const b = BADGE[state];
  const BIcon = b.icon;

  return (
    <div className="kw-fade space-y-6" data-testid="email-status">
      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-3">
          <Mail className="h-5 w-5" style={{ color: "var(--accent)" }} />
          <div className="min-w-0 flex-1">
            <div className="font-heading font-bold" style={{ color: "var(--text)" }}>E-Mail (Brevo)</div>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              {L({ de: "Transaktions-E-Mails: neue Anfrage, Freigabe/Ablehnung, Dokumentstatus, Rechnung. Nur aktiv, wenn ein API-Key hinterlegt ist.", fr: "E-mails transactionnels : nouvelle demande, approbation/refus, statut de document, facture. Actif seulement avec une clé API.", it: "Email transazionali: nuova richiesta, approvazione/rifiuto, stato documento, fattura. Attivo solo con una chiave API.", en: "Transactional emails: new inquiry, approval/rejection, document status, invoice. Active only when an API key is set." })}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full" data-testid="email-status-badge"
            style={{ background: b.bg, color: b.fg }}>
            <BIcon className="h-3.5 w-3.5" /> {L(b.label)}
          </span>
        </div>
        {!configured && (
          <div className="mt-4 p-3 text-xs flex items-start gap-2" style={{ background: "var(--surface-alt)", borderRadius: "var(--radius)", color: "var(--text-muted)" }}>
            <AlertTriangle className="h-4 w-4 shrink-0" style={{ color: "#B45309" }} />
            {L({ de: "Kein BREVO_API_KEY hinterlegt. Es werden keine E-Mails versendet – Ereignisse werden ehrlich als «nicht konfiguriert» protokolliert.", fr: "Aucune BREVO_API_KEY. Aucun e-mail envoyé – les événements sont journalisés « non configuré ».", it: "Nessuna BREVO_API_KEY. Nessuna email inviata – gli eventi sono registrati come «non configurato».", en: "No BREVO_API_KEY set. No emails are sent – events are honestly logged as ‘not configured’." })}
          </div>
        )}
      </Card>

      <div>
        <div className="text-sm font-semibold mb-3" style={{ color: "var(--text)" }}>{L({ de: "Versand-Protokoll", fr: "Journal d'envoi", it: "Registro invii", en: "Send log" })} ({log.length})</div>
        {log.length === 0 ? (
          <Card className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>{L({ de: "Noch keine E-Mail-Ereignisse.", fr: "Aucun événement.", it: "Nessun evento.", en: "No email events yet." })}</Card>
        ) : (
          <Card className="p-2">
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {log.map((e, i) => {
                const s = STATUS_STYLE[e.status] || STATUS_STYLE.skipped;
                return (
                  <div key={e.id || i} className="flex flex-wrap items-center gap-3 p-3" data-testid={`email-log-${i}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate" style={{ color: "var(--text)" }}>{e.subject || e.event}</div>
                      <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{e.portal_key} · {e.event} · {e.to_email}{e.error ? ` · ${e.error}` : ""}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full whitespace-nowrap" style={{ background: s.bg, color: s.fg }}>{e.status}</span>
                    <span className="text-xs whitespace-nowrap" style={{ color: "var(--text-muted)" }}>{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
